import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Calendar, DollarSign, TrendingUp, TrendingDown, CreditCard } from 'lucide-react';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import AIQueryBox from '../components/reports/AIQueryBox';
import { toast } from 'sonner';

const AccountingReport = () => {
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [aiResponse, setAiResponse] = useState(null);

  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  }, []);

  const handleQuery = async (query) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/reports/ai-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          reportType: 'accounting',
          dateRange
        })
      });

      if (!response.ok) throw new Error('Query failed');

      const data = await response.json();
      setAiResponse(data);
      toast.success('Query processed successfully!');
    } catch (error) {
      toast.error('Failed to process query');
      console.error(error);
    }
  };

  const metrics = [
    { label: 'Total Revenue', value: '$2.8M', change: '+18%', icon: DollarSign, color: 'from-green-500 to-green-600', trend: 'up' },
    { label: 'Outstanding AR', value: '$645K', change: '-12%', icon: TrendingDown, color: 'from-blue-500 to-blue-600', trend: 'down' },
    { label: 'Profit Margin', value: '24.5%', change: '+3.2%', icon: TrendingUp, color: 'from-purple-500 to-purple-600', trend: 'up' },
    { label: 'Pending Invoices', value: '42', change: '-8', icon: CreditCard, color: 'from-orange-500 to-orange-600', trend: 'down' },
  ];

  const lineChartData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    datasets: [
      {
        label: 'Revenue',
        data: [420000, 485000, 510000, 495000, 580000, 625000],
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        tension: 0.4
      },
      {
        label: 'Expenses',
        data: [320000, 365000, 385000, 375000, 435000, 470000],
        borderColor: 'rgb(239, 68, 68)',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        tension: 0.4
      }
    ]
  };

  const barChartData = {
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [{
      label: 'Quarterly Revenue ($K)',
      data: [1415, 1585, 1720, 1890],
      backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6']
    }]
  };

  const doughnutChartData = {
    labels: ['Paid', 'Pending', 'Overdue', 'Cancelled'],
    datasets: [{
      data: [720, 42, 18, 8],
      backgroundColor: ['#10B981', '#F59E0B', '#EF4444', '#6B7280']
    }]
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl shadow-lg p-8 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calculator className="w-8 h-8" />
            <div>
              <h1 className="text-3xl font-bold">Accounting Report</h1>
              <p className="text-white/90 text-lg">AI-powered financial analytics and revenue insights</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5" />
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <span className="text-white/70">to</span>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="px-3 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {metrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <motion.div
              key={index}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`bg-gradient-to-br ${metric.color} rounded-xl shadow-lg p-6 text-white`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon className="w-5 h-5" />
                <h3 className="text-sm font-medium opacity-80">{metric.label}</h3>
              </div>
              <p className="text-3xl font-bold">{metric.value}</p>
              <p className="text-sm opacity-80 mt-1">{metric.change} from last period</p>
            </motion.div>
          );
        })}
      </div>

      {/* AI Query Box */}
      <AIQueryBox onQuery={handleQuery} reportType="accounting" />

      {/* AI Response */}
      {aiResponse && (
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl shadow-sm p-6 border border-purple-200 dark:border-purple-800"
        >
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-3 flex items-center">
            <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            AI Insights
          </h3>
          <div className="prose dark:prose-invert max-w-none">
            <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{aiResponse.response}</p>
          </div>
        </motion.div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
        >
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Revenue vs Expenses</h3>
          <Line data={lineChartData} options={{ responsive: true, maintainAspectRatio: true }} />
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
        >
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Quarterly Revenue</h3>
          <Bar data={barChartData} options={{ responsive: true, maintainAspectRatio: true }} />
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
        >
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Invoice Status</h3>
          <Doughnut data={doughnutChartData} options={{ responsive: true, maintainAspectRatio: true }} />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AccountingReport;